import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { Layout } from "@/components/Layout";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import LoginPage from "@/pages/LoginPage";
import QaLogin from "@/pages/QaLogin";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Workspace from "@/pages/Workspace";
import AdminPanel from "@/pages/AdminPanel";
import Contact from "@/pages/Contact";
import JourneyPage from "@/pages/JourneyPage";
import CustomPage from "@/pages/CustomPage";
import BlogIndex from "@/pages/BlogIndex";
import MemberDataRoom from "@/pages/MemberDataRoom";
import MyDataRooms from "@/pages/MyDataRooms";
import PublicEventPage from "@/pages/PublicEventPage";
import NotFound from "@/pages/NotFound";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect session_id in URL hash during render (not useEffect) to
  // avoid race conditions with ProtectedRoute / AuthProvider's initial /auth/me check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <AnalyticsTracker />
      <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        {/* /explorer removed — feature retired */}
        <Route path="/explorer" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={<Contact />} />
        {/* /waitlist is superseded by the guided journey — deep-link
            straight to a starter vertical so visitors never land on an
            empty picker. */}
        <Route path="/waitlist" element={<Navigate to="/journey?type=retail-storefront" replace />} />
        <Route path="/journey" element={<JourneyPage />} />
        <Route path="/e/:token" element={<PublicEventPage />} />
        <Route path="/p/:slug" element={<CustomPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route
          path="/data-rooms"
          element={
            <ProtectedRoute>
              <MyDataRooms />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-rooms/:roomId"
          element={
            <ProtectedRoute>
              <MemberDataRoom />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {process.env.REACT_APP_ENABLE_QA === "true" && (
          <Route path="/qa-login" element={<QaLogin />} />
        )}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requireOnboarding={false}>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <Workspace />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* Admin console lives outside <Layout> — it ships its own full-bleed
          chrome (rail + top bar) instead of the public header/footer. */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" enableSystem={false}>
        <BrowserRouter>
          <AuthProvider>
            <SiteContentProvider>
              <NotificationsProvider>
                <div className="App">
                  <AppRoutes />
                  <Toaster richColors position="top-center" />
                </div>
              </NotificationsProvider>
            </SiteContentProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
